import { Spine } from 'pixi-spine';
import { Assets, type Container } from 'pixi.js';
import SegmentedHealthBar from '~/components/Halloween/lib/segmented-health-bar';

export type CharacterIndexes = 1 | 2 | 3 | 4 | 5 | 6;

export interface HalloweenCharacterConstructorOptions {
  layer: Container;
  characterIndex: CharacterIndexes;
  positionInBatch: number;
  baseZIndex?: number;
}

export default class HalloweenCharacter {
  #spine?: Spine;
  #healthBar?: SegmentedHealthBar;
  #direction: 'left' | 'right' = 'right';

  constructor(private readonly options: HalloweenCharacterConstructorOptions) {}

  public createCharacterSprite() {
    const spineData = Assets.cache.get(`character_${this.options.characterIndex}`) as any;
    if (!spineData) throw new Error('HalloweenCharacterError: Character assets not loaded');

    const spine = new Spine(spineData.spineData);
    spine.scale.set(0.25);
    spine.zIndex = (this.options.baseZIndex ?? 10) + this.options.positionInBatch;

    const healthBar = new SegmentedHealthBar();
    healthBar.scale.set(0.7);
    healthBar.zIndex = (this.options.baseZIndex ?? 10) + 10 + this.options.positionInBatch;

    this.#spine = spine;
    this.#healthBar = healthBar;

    this.options.layer.addChild(healthBar);
    this.options.layer.addChild(spine);
    spine.visible = false;

    return this;
  }

  public setWalkAnimation(direction: 'left' | 'right', speed = 1) {
    if (!this.#spine) throw new Error('HalloweenCharacterError: Character not created');

    const animationIndex = this.#spine.skeleton.data.animations.findIndex((animation) => {
      return (
        animation.name.toLowerCase().includes(direction) ||
        animation.name.toLowerCase().includes(direction.slice(0, -1))
      );
    });

    if (animationIndex === -1) throw new Error('HalloweenCharacterError: Animation not found');
    this.#direction = direction;
    this.#spine.state.setAnimation(animationIndex, this.#spine.skeleton.data.animations[animationIndex].name, true);
    this.#spine.state.timeScale = speed;

    return this;
  }

  public async setVisible(visible: boolean) {
    if (!this.#spine) throw new Error('HalloweenCharacterError: Character not created');
    this.#spine.visible = visible;

    return this;
  }

  public setHealth(health: number) {
    if (!this.#healthBar) return;
    this.#healthBar.health = health / 100;
  }

  public get $spine() {
    return this.#spine;
  }

  public get $healthBar() {
    return this.#healthBar;
  }

  public get $direction() {
    return this.#direction;
  }
}
